import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox } from '../../design-system/primitives/Combobox/Combobox';
import type { FieldWidth } from '../../design-system/primitives/Field/Field';
import { ApiError } from '../../lib/api/client';
import { lookupsApi, type RoleLookup } from '../../lib/api/lookups';
import { roleOptionLabel } from './pickerLabels';

/**
 * RolePicker — choose a role by its label, hand back its uuid.
 *
 * Replaces the escalation-routes editor's "Role UUID" text box. Backed by
 * `GET /lookups/roles` (`escalation.manage`), not `GET /roles`, which needs
 * `user.role.assign` — see `lib/api/lookups.ts`.
 *
 * The catalogue is a handful of rows, so it is fetched ONCE on mount and
 * filtered locally by the ordinary Combobox, unlike `StaffPicker`. Both
 * labels arrive, so the visible one follows the UI language with no refetch
 * (the `startsWith('ar')` rule `referenceName()` uses).
 */

export interface RolePickerProps {
  label: string;
  width: FieldWidth;
  /** The chosen role's uuid, or null. */
  value: string | null;
  onChange: (uuid: string) => void;
  /** Shown for `value` until the catalogue arrives (e.g. a saved route's `role_name`). */
  selectedLabel?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export const RolePicker = forwardRef<HTMLInputElement, RolePickerProps>(function RolePicker(
  { label, width, value, onChange, selectedLabel, error, required, disabled, id, className },
  ref,
) {
  const { t, i18n } = useTranslation();
  const [roles, setRoles] = useState<readonly RoleLookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const rows = await lookupsApi.roles(controller.signal);
        if (controller.signal.aborted) return;
        setRoles(rows);
      } catch (failure) {
        if (controller.signal.aborted) return;
        if (!(failure instanceof ApiError)) throw failure;
        setFailed(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, []);

  const options = useMemo(
    () => roles.map((role) => ({ value: role.uuid, label: roleOptionLabel(role, i18n.language) })),
    [roles, i18n.language],
  );

  return (
    <Combobox
      ref={ref}
      label={label}
      width={width}
      options={options}
      value={value}
      selectedLabel={selectedLabel}
      emptyMessage={failed ? t('pickers.roleLoadFailed') : t('pickers.roleEmpty')}
      loading={loading}
      loadingMessage={t('list.loading')}
      onChange={onChange}
      error={error}
      required={required}
      disabled={disabled}
      id={id}
      className={className}
    />
  );
});
